import React, { useEffect } from 'react';

import { MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ContractItem } from '@/api';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import CardGererContrat from '@/components/CardGererContrat/CardGererContrat';
import CardProduct from '@/components/CardProduct/CardProduct';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import { HIDDEN_PERFORMANCE_GESTIONNAIRE_IDS } from '@/constants/gestionnaire';
import { ROUTE } from '@/constants/route';
import { useAuth } from '@/hooks/useAuth';
import { useListingContrats } from '@/hooks/useListingContrats';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { ContentModalVersementLivret } from '@/modals/ContentModalVersementLivret/ContentModalVersementLivret';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';

import styles from './styles.module.css';

export const SelectContract: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { state } = useLocation();
  const key = state?.key;

  const [showModalVersementLivret, setShowModalVersementLivret] = React.useState(false);
  const { data, isLoading, isError } = useListingContrats();
  type RouteKey = keyof typeof ROUTE;
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Mes opérations', path: ROUTE.OPERATIONS },
    { label: 'Choisir un contrat', path: ROUTE.SELECTCONTRACT, key: key },
  ];

  if (state?.retry) {
    if (data) {
      const monPlacement = data?.listContrat?.find((contrat) => contrat.placementId === state.idPlacement);
      navigate(ROUTE.SELECTOPERATION, {
        state: {
          key: key,
          breadcrumbItems: breadcrumbItems,
          infoContrat: monPlacement,
        },
      });
    }
  }

  const handleClick = (contrat: ContractItem) => {
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: `${contrat.famille} - ${contrat.nomProduit}`,
      event_detail_1: 'carte',
      event_detail_2: `${contrat.famille} - ${contrat.nomProduit}`,
    });

    if (contrat.famille === 'LIVRET' && key === 'versement') {
      openModal('showModalVersementLivret');
    } else {
      navigate(ROUTE.SELECTOPERATION, {
        state: {
          key: key,
          breadcrumbItems: breadcrumbItems,
          infoContrat: contrat,
        },
      });
    }
  };

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    if (modal === 'showModalVersementLivret') {
      setShowModalVersementLivret(true);
    }
  };

  const closeModal = () => {
    setShowModalVersementLivret(false);
  };

  if (sessionStorage.parcoursVersementData) {
    sessionStorage.removeItem('parcoursVersementData');
  }

  useEffect(() => {
    if (user?.id && user.type && user.success && key) {
      if (key === 'rachat/retrait') {
        TRACKING_INSTANCE.setTcVars({
          page_nom: 'rachat - choisir un contrat',
          page_template: 'espace client',
          user_id: user.id,
          user_client: user.type === 'Client',
          user_connexion: user.success,
          user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
        });
        void TRACKING_INSTANCE.triggerEvent('page', undefined, {
          event_id: 'page',
          event_categorie: 'affichage espace client',
          event_nom: 'rachat - choisir un contrat',
          event_page: 'rachat - choisir un contrat',
        });
      } else {
        TRACKING_INSTANCE.setTcVars({
          page_nom: key + ' - choisir un contrat',
          page_template: 'espace client',
          user_id: user.id,
          user_client: user.type === 'Client',
          user_connexion: user.success,
          user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
        });
        void TRACKING_INSTANCE.triggerEvent('page', undefined, {
          event_id: 'page',
          event_categorie: 'affichage espace client',
          event_nom: key + ' - choisir un contrat',
          event_page: key + ' - choisir un contrat',
        });
      }
    }
  }, [user?.id, user?.type, user?.success, key]);

  if (isLoading) {
    return <Loader />;
  }
  if (isError) navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.OPERATIONS } });

  return (
    <MtxGrid className={styles.selectContract}>
      {showModalVersementLivret && (
        <ModaleDefaut
          title={t('operations.modalTitle')}
          content={ContentModalVersementLivret}
          btnLabel="J'ai compris"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={() => closeModal()}
          btnFullwidth={false}
          onClickClose={() => closeModal()}
          mobileStyle="stick-to-bottom"
          size="large"
          infoCircle={true}
        />
      )}
      <MtxGrid className={styles.selectContractHead} row>
        <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
          <Breadcrumb items={breadcrumbItems} />
        </MtxGrid>
        <MtxGrid
          className={styles.selectContractHeadTitle}
          col={12}
          colXs={12}
          colSm={12}
          colMd={12}
          colLg={12}
          colXl={12}
        >
          <h1>{t('operations.selectContract')}</h1>
          <p>
            {key ? (
              <>
                {t('operations.description')}
                {key}.
              </>
            ) : (
              t('operations.descriptionDefault')
            )}
          </p>
        </MtxGrid>
      </MtxGrid>

      <MtxGrid row>
        {data?.listContrat?.map((contract) => {
          const contractRoute = contract.famille && ROUTE[contract.famille as RouteKey];
          const isPerformanceHidden = HIDDEN_PERFORMANCE_GESTIONNAIRE_IDS.includes(contract.idGestionnaire ?? '');
          const evolution = isPerformanceHidden ? null : contract.evolution;
          return (
            <MtxGrid
              className={styles.selectContractItem}
              col={4}
              colXs={12}
              colSm={6}
              colMd={6}
              colLg={4}
              colXl={4}
              key={contract.placementId}
            >
              {contractRoute ? (
                <CardProduct
                  family={contract.famille}
                  insuranceLogo={contract.gestionnaireLogo ?? ''}
                  amount={contract.valorisation?.encours ?? 0}
                  contractName={contract.nomProduit}
                  performance={evolution?.performance ?? null}
                  gainLoss={evolution?.plusMoinsValue ?? null}
                  distributionRate={contract.tauxDistribution}
                  status={contract.statut}
                  onClick={() => {
                    handleClick(contract);
                  }}
                />
              ) : (
                <CardGererContrat
                  family={contract.famille}
                  insuranceLink={contract.gestionnaireAC ?? ''}
                  insuranceLogo={contract.gestionnaireLogo}
                  contractName={contract.nomProduit}
                  isLogo={true}
                  isAsideInfo={false}
                  operation={key}
                />
              )}
            </MtxGrid>
          );
        })}
      </MtxGrid>
    </MtxGrid>
  );
};
